import { motion } from 'framer-motion';
import { SITE_LOGO } from '../../constants/assets';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function SobreNosotros() {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const brands = [
    'Sandro', 'ETRO', 'Claudie Pierlot', 'Zegna', 
    'Moncler', 'Zimmermann', 'Burberry'
  ];

  const galleryImages = [
    '/assets/about/gallery-1.jpg',
    '/assets/about/gallery-2.jpg',
    '/assets/about/gallery-3.jpg',
    '/assets/about/gallery-4.jpg',
    '/assets/about/gallery-5.jpg',
    '/assets/about/gallery-7.jpg',
    '/assets/about/gallery-8.jpg',
    '/assets/about/gallery-9.jpg',
    '/assets/about/gallery-10.jpg',
    '/assets/about/gallery-11.jpg',
    '/assets/about/gallery-12.jpg',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-24 lg:pt-28">
        {/* Hero Section */}
        <section className="relative h-[80vh] flex items-center justify-center overflow-hidden">
          <motion.div 
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.5 }}
            className="absolute inset-0 z-0"
          >
            <img 
              src="/assets/about/hero-about.jpg" 
              alt="Atelier Anthony Sánchez" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-brand-black/30" />
          </motion.div>
          
          <div className="relative z-10 text-center px-6">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="font-serif text-white text-4xl md:text-6xl lg:text-7xl font-light tracking-tight"
            >
              Anthony Sánchez
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 1 }}
              className="font-sans text-white/80 text-sm md:text-base uppercase tracking-[0.3em] mt-6"
            >
              El arte de vestir la ciudad
            </motion.p>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-24 px-6 lg:px-[15%]">
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto space-y-12">
            <div className="space-y-6">
              <h2 className="font-serif text-brand-black text-3xl md:text-4xl leading-tight">
                Rambla de Catalunya 85: Donde la moda se convierte en experiencia.
              </h2>
              <p className="font-sans text-brand-gray-600 text-lg leading-relaxed italic">
                "En Barcelona, una ciudad donde el diseño, la arquitectura y la cultura se entrelazan en cada esquina, algunas direcciones son mucho más que un lugar: son un escenario."
              </p>
              <p className="font-sans text-brand-gray-600 text-lg leading-relaxed">
                Esta emblemática avenida, históricamente punto de encuentro de creatividad y estilo, ahora alberga el nuevo atelier de Anthony Sánchez, un espacio que simboliza un nuevo inicio en su trayectoria y redefine cómo se vive la alta costura en la ciudad.
              </p>
            </div>

            <div className="bg-brand-cream/50 rounded-[40px] p-8 md:p-12 border border-brand-gray-100">
              <p className="font-sans text-brand-black text-xl leading-relaxed font-light">
                Al cruzar sus puertas, el ritmo urbano queda atrás: las telas parecen respirar, los hilos trazan recorridos invisibles y las manos de las modistas, expertas y meticulosas, potenciamos cada idea del diseñador, convirtiendo cada concepto en prenda viva.
              </p>
            </div>
          </motion.div>
        </section>

        {/* The Designer Section */}
        <section className="py-24 bg-brand-gray-50 px-6 lg:px-[15%]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp} className="space-y-8">
              <h3 className="font-serif text-brand-black text-3xl md:text-4xl">La Trayectoria</h3>
              <p className="font-sans text-brand-gray-600 text-lg leading-relaxed">
                Formado en la Escuela de Javier Barroeta, Anthony construyó su lenguaje desde la base más sólida de la alta costura —patronaje, moulage, bordado— y lo expandió hacia un territorio más sensorial y emocional, donde cada diseño cuenta una historia.
              </p>
              <p className="font-sans text-brand-gray-600 text-lg leading-relaxed">
                Su trabajo interpreta cuerpos, identidades y momentos, transformando la vestimenta en narrativa.
              </p>
            </motion.div>
            <motion.div 
              {...fadeInUp}
              className="aspect-[4/5] rounded-[32px] overflow-hidden shadow-2xl"
            >
              <img 
                src="/assets/about/anthony-portrait.jpg" 
                alt="Anthony Sánchez" 
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </section>

        {/* Collaborations & Luxury Section */}
        <section className="py-24 px-6 lg:px-[15%]">
          <div className="max-w-5xl mx-auto space-y-20">
            <motion.div {...fadeInUp} className="text-center space-y-8">
              <h3 className="font-serif text-brand-black text-3xl md:text-5xl">Confianza y Excelencia</h3>
              <p className="font-sans text-brand-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
                Anthony Sánchez ha creado piezas a medida para destacados artistas como <strong>Aldo Comas, Nacho, Juan Magán y Maria Elisa Peralta</strong>.
              </p>
              <div className="p-8 rounded-[32px] bg-brand-black text-white space-y-4">
                <p className="font-sans text-sm uppercase tracking-[0.2em] text-brand-sage">Premios Goya 2026</p>
                <p className="font-serif text-xl md:text-2xl font-light italic">
                  "Un trabajo artesanal altamente personalizado, basado en confianza, precisión en el detalle y la capacidad de transformar cada diseño en una narrativa única."
                </p>
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="space-y-12">
              <div className="text-center">
                <h4 className="font-sans text-xs uppercase tracking-[0.3em] text-brand-gray-400 mb-8">
                  Colaborando con marcas de lujo internacionales
                </h4>
                <div className="flex flex-wrap justify-center gap-x-12 gap-y-8">
                  {brands.map(brand => (
                    <span key={brand} className="font-serif text-2xl text-brand-black/60 hover:text-brand-black transition-colors cursor-default">
                      {brand}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ETERNO Section */}
        <section className="relative py-32 bg-brand-black text-white overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full opacity-10 pointer-events-none">
            <div className="w-full h-full border-l border-white/20 transform rotate-12" />
          </div>
          
          <div className="relative z-10 px-6 lg:px-[15%] max-w-7xl mx-auto">
            <motion.div {...fadeInUp} className="space-y-12">
              <div className="space-y-4">
                <h3 className="font-serif text-4xl md:text-6xl font-light">ETERNO</h3>
                <p className="font-sans text-brand-sage text-sm uppercase tracking-[0.4em]">La tierra como inspiración</p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 text-lg font-light leading-relaxed text-white/80">
                <p>
                  El último desfile de Anthony Sánchez en el Hotel ME Barcelona marcó un hito en su narrativa artística. La colección surge de un diálogo con la tierra y su poder ancestral, explorando la relación entre el ser humano, la naturaleza y la moda como experiencia sensorial.
                </p>
                <p>
                  “Texturas que sostienen, líneas que esculpen y tonos que conectan con lo esencial”, explica Anthony. Cada prenda está diseñada para estructurar, sostener y empoderar, convirtiéndose en un manifiesto visual que habla de fuerza, raíz y permanencia.
                </p>
              </div>

              <div className="border-t border-white/10 pt-12">
                <p className="font-serif text-2xl md:text-3xl font-light italic leading-relaxed text-brand-sage">
                  "El primer diseño de esta colección nace directamente 'desde la raíz 🌾', un gesto simbólico que define la narrativa de ETERNO: firme, orgánico y atemporal."
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className="py-24 bg-white px-6 lg:px-[10%]">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h3 className="font-serif text-brand-black text-3xl md:text-4xl">Universo Anthony Sánchez</h3>
            <div className="w-20 h-px bg-brand-sage mx-auto mt-6" />
          </motion.div>
          
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
            {galleryImages.map((src, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="break-inside-avoid rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer"
              >
                <img 
                  src={src} 
                  alt={`Galería ${index + 1}`} 
                  className="w-full h-auto object-cover hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
            ))}
          </div>
        </section>

        {/* Final Statement */}
        <section className="py-32 px-6 lg:px-[15%] text-center">
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto space-y-12">
            <h3 className="font-serif text-brand-black text-3xl md:text-4xl leading-tight">
              La alta costura se siente, se vive y se experimenta.
            </h3>
            <p className="font-sans text-brand-gray-600 text-lg leading-relaxed">
              Con su nuevo atelier como símbolo de un nuevo inicio, su capacidad de conectar tradición, técnica y emoción reafirma a Anthony Sánchez como uno de los diseñadores de alta costura más auténticos y relevantes de Barcelona.
            </p>
            <div className="pt-12">
              <img 
                src={SITE_LOGO} 
                alt="Logo Anthony Sánchez" 
                className="h-20 mx-auto opacity-20 grayscale"
              />
            </div>
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
